import { forwardRef } from "react";
import { Trophy, Medal, Star } from "lucide-react";

const SEASONS = [1, 2, 3, 4, 5];

/**
 * Branded 1080x1350 (rendered 540x675, exported @2x) player stat card
 * for sharing to WhatsApp / Instagram. Rendered off-screen by Players.jsx.
 */
const StatCard = forwardRef(function StatCard({ player, career, titles, runnerUps, awards }, ref) {
  const totals = career?.totals || { runs: 0, wickets: 0, dismissals: 0, mvp_points: 0 };
  const initials = player.name.split(" ").map((n) => n[0]).slice(0, 2).join("");

  return (
    <div
      ref={ref}
      data-testid="stat-card"
      className="relative w-[540px] h-[675px] overflow-hidden font-sans"
      style={{
        background: "radial-gradient(ellipse 90% 60% at 80% -10%, #004aad 0%, rgba(0,74,173,0) 55%), #090a0b",
        color: "#ffffff",
      }}
    >
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 8, background: "#ff3b30" }} />

      <div className="flex items-center gap-3 px-10 pt-10">
        <img src="/asl-logo.png" alt="" className="h-14 w-auto" crossOrigin="anonymous" />
        <div>
          <p className="font-display text-2xl tracking-wide leading-none">ALL STAR LEAGUE</p>
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] mt-1.5" style={{ color: "#ff3b30" }}>Official Player Card</p>
        </div>
        <div className="ml-auto text-right">
          <p className="font-oswald text-base font-semibold" style={{ color: "rgba(255,255,255,0.5)" }}>S1.0 — S5.0</p>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] mt-1" style={{ color: "rgba(255,255,255,0.3)" }}>Career Record</p>
        </div>
      </div>

      <div className="px-10 mt-10 flex items-center gap-5">
        <span
          className="w-20 h-20 rounded-full flex items-center justify-center font-oswald text-3xl font-semibold shrink-0"
          style={{ background: "#004aad" }}
        >
          {initials}
        </span>
        <div className="min-w-0">
          <p className="font-display text-[44px] tracking-tight leading-none">{player.name.toUpperCase()}</p>
          <p className="text-xs font-bold uppercase tracking-[0.25em] mt-2.5" style={{ color: "rgba(255,255,255,0.5)" }}>
            {player.season_count} season{player.season_count > 1 ? "s" : ""} • Latest — {player.latest_team}
          </p>
        </div>
      </div>

      {(titles > 0 || runnerUps > 0 || (awards && awards.length > 0)) && (
        <div className="px-10 mt-6 flex flex-wrap gap-2.5">
          {titles > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest" style={{ background: "rgba(251,191,36,0.15)", border: "1px solid rgba(251,191,36,0.5)", color: "#fbbf24" }}>
              <Trophy size={12} /> Champion ×{titles}
            </span>
          )}
          {runnerUps > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.7)" }}>
              <Medal size={12} /> Runner-up ×{runnerUps}
            </span>
          )}
          {(awards || []).slice(0, 2).map((a) => (
            <span key={a.label} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest text-white" style={{ background: a.color }}>
              <Star size={11} /> {a.label}
            </span>
          ))}
        </div>
      )}

      <div className="px-10 mt-9 grid grid-cols-4 gap-3.5">
        {[
          ["Runs", totals.runs],
          ["Wickets", totals.wickets],
          ["Dismissals", totals.dismissals],
          ["MVP Pts", totals.mvp_points],
        ].map(([label, val]) => (
          <div key={label} className="rounded-xl px-2 py-6 text-center" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <p className="font-oswald text-3xl font-semibold leading-none" style={{ color: "#ff3b30" }}>{val}</p>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] mt-2.5" style={{ color: "rgba(255,255,255,0.45)" }}>{label}</p>
          </div>
        ))}
      </div>

      <div className="px-10 mt-9">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] mb-3" style={{ color: "rgba(255,255,255,0.35)" }}>Season Journey</p>
        <div className="flex gap-2.5">
          {SEASONS.map((s) => {
            const played = player.seasons.find((x) => x.season === s);
            return (
              <span
                key={s}
                className="flex-1 text-center py-3 rounded-md text-xs font-bold uppercase tracking-wider"
                style={played ? { background: "#004aad", color: "#fff" } : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.25)" }}
              >
                S{s}.0
              </span>
            );
          })}
        </div>
        <p className="text-[9px] font-semibold uppercase tracking-[0.25em] mt-6 text-center" style={{ color: "rgba(255,255,255,0.25)" }}>
          Records via official CricHeroes leaderboards
        </p>
      </div>

      <div className="absolute bottom-0 left-0 right-0 px-10 py-6 flex items-center justify-between" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <p className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: "rgba(255,255,255,0.4)" }}>
          All Star League 6.0 • Surat
        </p>
        <p className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: "#ff3b30" }}>
          19–21 Feb 2027
        </p>
      </div>
    </div>
  );
});

export default StatCard;
